import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/constants";
import { toast } from "sonner";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (n) => {
    try {
      if (!n.read) await api.post(`/notifications/${n.id}/read`);
      if (n.order_id) navigate(`/orders/${n.order_id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
      toast.success("All notifications marked as read.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="notifications-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">// Inbox</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Notifications</h1>
        </div>
        <button onClick={markAll} className="btn-ghost inline-flex items-center gap-2" data-testid="mark-all-read">
          <CheckCircle size={16} weight="bold" /> Mark all read
        </button>
      </header>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : items.length === 0 ? (
        <div className="grid-line bg-white p-12 text-center">
          <Bell size={32} weight="duotone" color="#2563EB" className="mx-auto mb-3" />
          <div className="overline mb-1">All caught up</div>
          <div className="text-sm text-[var(--muted)]">No notifications right now.</div>
        </div>
      ) : (
        <div className="grid-line bg-white divide-y divide-[var(--line)]">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => markRead(n)}
              className={`w-full text-left p-4 flex gap-3 hover:bg-[#F8FAFC] transition-colors ${!n.read ? "bg-[#EFF6FF]" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              <div className="mt-1">
                <Bell size={18} weight={n.read ? "regular" : "fill"} color={n.read ? "#94A3B8" : "#2563EB"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-bold text-sm">{n.title}</div>
                  <div className="text-[0.7rem] text-[var(--muted)] shrink-0">{formatDateTime(n.created_at)}</div>
                </div>
                <div className="text-sm text-[var(--ink-soft)] mt-0.5">{n.body}</div>
                {n.order_ref && (
                  <div className="text-[0.7rem] font-mono-tech text-[var(--solar)] mt-1">{n.order_ref}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
